import React, { useState, useEffect } from 'react';
import {
Container,
Typography,
Button,
Paper,
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Card,
CardContent,
Tabs,
Tab,
Alert,
LinearProgress,
CircularProgress,
Tooltip,
Menu
} from '@mui/material';
import {
PictureAsPdf,
TableChart,
GetApp,
FilterList,
Refresh,
Today,
Close,
Search,
Download,
TrendingUp,
TrendingDown,
Inventory,
AttachMoney,
Storage,
Warning,
CheckCircle,
Error,
CalendarMonth,
BarChart,
PieChart,
Summarize,
ShoppingCart,
Store,
LocalShipping,
Category,
Assessment,
Analytics
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import {
APIIngredientInventoryReport,
APISupplierNames,APIIngredientNames} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR } from '../../../services/Text';
import {formatNumberWithComma,getCategoriesNameById,
    getUserDetails,formatCurrency} from '../../../utils/helpers';
import exportInventoryReport, {
exportToExcel,
exportToPDF,
exportToCSV
} from '../../../services/restaurant/InventoryReportService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { apiService } from '../../../apiServices/ApiService';

const InventoryReport = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [exportAnchorEl, setExportAnchorEl] = useState(null);

const [reportData, setReportData] = useState({
    summary: {
    total_items: 0,
    total_quantity: 0,
    total_cost_value: 0,
    total_selling_value: 0,
    total_profit: 0,
    average_cost_per_item: 0,
    average_selling_per_item: 0,
    suppliers_count: 0,
    stock_items_count: 0
    },
    details: [],
    topSuppliers: [],
    topStockItems: [],
    lowQuantityItems: [],
    highValueItems: [],
    trends: [],
    dateRange: {}
});

const [filters, setFilters] = useState({
    branch_id: '',
    supplier_id: '',
    stock_id: '',
    min_quantity: '',
    max_quantity: '',
    min_cost: '',
    max_cost: '',
    min_selling: '',
    max_selling: '',
    dateFrom: '',
    dateTo: '',
    report_type: 'detailed', // detailed, summary, supplier,  stock
    sort_by: 'total_value', // total_value, quantity, profit, cost, selling
    sort_order: 'desc' // asc, desc
});

const [referenceData, setReferenceData] = useState({
    branchNames: [],unitsNames:[],
    supplierNames: [],stockNames: []
});

const [selectedBranch, setSelectedBranch] = useState('');
const [activeTab, setActiveTab] = useState(0);
const [userRole, setUserRole] = useState('');

useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
    fetchReferenceData();
    generateReport();
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

const fetchReferenceData = async () => {
    try {
    // Fetch all reference data in parallel
    const [branchesResult, suppliersResult, stocksResult] = await Promise.all([
        apiService.branchNames(),
        apiService.listNames(APISupplierNames),
        apiService.listNames(APIIngredientNames)
    ]);

    setReferenceData({
        branchNames: branchesResult.success ? branchesResult.data : [],
        supplierNames: suppliersResult.success ? suppliersResult.data : [],
        stockNames: stocksResult.success ? stocksResult.data : [],
    });
    } catch (error) {
    showToast("Error", "Failed to load reference data", "error");
    }
};

const generateReport = async () => {
    setLoading(true);
    setError(null);

    try {
    let branchIdToUse = filters.branch_id;
    if (!hasPermission(PERMISSIONS.BRANCHES) && !branchIdToUse) {
        branchIdToUse = getUserDetails("branchId");
    }

    const params = {
        ...filters,
        branch_id: branchIdToUse || ''
    };

    // Clean up empty params
    Object.keys(params).forEach(key => {
        if (!params[key] && params[key] !== 0) delete params[key];
    });
    // Use the centralized API service
    const response = await axios.get(APIIngredientInventoryReport, params);

    if (response.data.status === 'success') {
        setReportData(response.data.data);
        showToast("Success", "Report generated successfully", "success");
    } else {
        throw new Error(response.data.message || "Failed to generate report");
    }
    } catch (error) {
    let errorMessage = "Failed to generate report";
    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }
    setError(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const handleDateRangeChange = (type, value) => {
    setFilters(prev => ({
    ...prev,
    [type]: value
    }));
};

const applyToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
};

const applyThisWeek = () => {
    const today = new Date();
    const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
    const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));
    
    setFilters({
    ...filters,
    dateFrom: firstDay.toISOString().split('T')[0],
    dateTo: lastDay.toISOString().split('T')[0]
    });
};

const applyThisMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters({
    ...filters,
    dateFrom: firstDay.toISOString().split('T')[0],
    dateTo: lastDay.toISOString().split('T')[0]
    });
};

const applyLastMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    
    setFilters({
    ...filters,
    dateFrom: firstDay.toISOString().split('T')[0],
    dateTo: lastDay.toISOString().split('T')[0]
    });
};

const clearFilters = () => {
    setFilters({
    branch_id: '',
    supplier_id: '',
    stock_id: '',
    min_quantity: '',
    max_quantity: '',
    min_cost: '',
    max_cost: '',
    min_selling: '',
    max_selling: '',
    dateFrom: '',
    dateTo: '',
    report_type: 'detailed',
    sort_by: 'total_value',
    sort_order: 'desc'
    });
    setSelectedBranch('');
};

const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
    setExportAnchorEl(null);
};

const handleExportExcel = async () => {
  try {
    handleExportClose();
    const result = await exportInventoryReport.toExcel(reportData, filters, referenceData);
    showToast("Success", `Excel report downloaded: ${result.fileName}`, "success");
  } catch (error) {
    showToast("Error", error.message, "error");
  }
};

const handleExportPDF = async () => {
    try {
    handleExportClose();
    await exportToPDF(reportData, filters, referenceData);
    showToast("Success", "PDF report downloaded successfully", "success");
    } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportCSV = async () => {
    try {
    handleExportClose();
    await exportToCSV(reportData, filters, referenceData);
    showToast("Success", "CSV report downloaded successfully", "success");
    } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
    }
};

const getProfitColor = (profit) => {
    if (profit > 0) return theme.palette.success.main;
    if (profit < 0) return theme.palette.error.main;
    return theme.palette.grey[500];
};

const getQuantityColor = (quantity) => {
    if (quantity <= 0) return theme.palette.error.main;
    if (quantity <= 10) return theme.palette.warning.main;
    if (quantity <= 50) return theme.palette.success.main;
    return theme.palette.info.main;
};

const getQuantityLevel = (quantity) => {
    if (quantity <= 0) return 'Out of Stock';
    if (quantity <= 10) return 'Low';
    if (quantity <= 50) return 'Normal';
    return 'High';
};

const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
};

const calculateItemProfit = (item) => {
    return (item.selling - item.cost) * item.quantity;
};

const calculateItemTotalValue = (item) => {
    return item.quantity * item.selling;
};

if (loading && !reportData.details.length) {
    return (
    <Container maxWidth="xl" sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box textAlign="center">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Generating Club Inventory Report...</Typography>
        </Box>
    </Container>
    );
}

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Restaurant Inventory Analysis Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
            Comprehensive inventory analysis with supplier and stock insights
        </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={generateReport}
        >
            Refresh Report
        </Button>
        <Button
            variant="contained"
            color="success"
            startIcon={<GetApp />}
            onClick={handleExportClick}
        >
            Export Report
        </Button>
        </Box>
    </Box>

    {/* Filters Section */}
    <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterList /> Report Filters
        </Typography>
        
        <Grid container spacing={3}>
        {/* Quick Date Filters */}
        <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
                variant="outlined"
                startIcon={<Today />}
                onClick={applyToday}
            >
                Today
            </Button>
            <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={applyThisWeek}
            >
                This Week
            </Button>
            <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={applyThisMonth}
            >
                This Month
            </Button>
            <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={applyLastMonth}
            >
                Last Month
            </Button>
            <Button
                variant="outlined"
                color="error"
                startIcon={<Close />}
                onClick={clearFilters}
            >
                Clear All
            </Button>
            </Box>
        </Grid>

        {/* Branch Filter (for super admin) */}
        {hasPermission(PERMISSIONS.BRANCHES) && (
            <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                name="branch_id"
                value={filters.branch_id}
                onChange={handleFilterChange}
                label="Branch"
                >
                <MenuItem value="">All Branches</MenuItem>
                {referenceData.branchNames.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                    </MenuItem>
                ))}
                </Select>
            </FormControl>
            </Grid>
        )}

        {/* Supplier Filter */}
        <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
            <InputLabel>Supplier</InputLabel>
            <Select
                name="supplier_id"
                value={filters.supplier_id}
                onChange={handleFilterChange}
                label="Supplier"
            >
                <MenuItem value="">All Suppliers</MenuItem>
                {referenceData.supplierNames.map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        </Grid>


        {/* Stock Item Filter */}
        <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
            <InputLabel>Stock Item</InputLabel>
            <Select
                name="stock_id"
                value={filters.stock_id}
                onChange={handleFilterChange}
                label="Stock Item"
            >
                <MenuItem value="">All Stock Items</MenuItem>
                {referenceData.stockNames.map((stock) => (
                <MenuItem key={stock.id} value={stock.id}>
                    {stock.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        </Grid>

        {/* Quantity Range */}
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="Min Quantity"
            type="number"
            name="min_quantity"
            value={filters.min_quantity}
            onChange={handleFilterChange}
            size="small"
            InputProps={{ inputProps: { min: 0 } }}
            />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="Max Quantity"
            type="number"
            name="max_quantity"
            value={filters.max_quantity}
            onChange={handleFilterChange}
            size="small"
            InputProps={{ inputProps: { min: 0 } }}
            />
        </Grid>

        {/* Cost Range */}
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="Min Cost"
            type="number"
            name="min_cost"
            value={filters.min_cost}
            onChange={handleFilterChange}
            size="small"
            InputProps={{ inputProps: { min: 0, step: "0.01" } }}
            />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="Max Cost"
            type="number"
            name="max_cost"
            value={filters.max_cost}
            onChange={handleFilterChange}
            size="small"
            InputProps={{ inputProps: { min: 0, step: "0.01" } }}
            />
        </Grid>

        {/* Date Range */}
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="From Date"
            type="date"
            name="dateFrom"
            value={filters.dateFrom}
            onChange={(e) => handleDateRangeChange('dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="To Date"
            type="date"
            name="dateTo"
            value={filters.dateTo}
            onChange={(e) => handleDateRangeChange('dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            />
        </Grid>

        {/* Report Type */}
        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Report Type</InputLabel>
            <Select
                name="report_type"
                value={filters.report_type}
                onChange={handleFilterChange}
                label="Report Type"
            >
                <MenuItem value="detailed">Detailed</MenuItem>
                <MenuItem value="summary">Summary</MenuItem>
                <MenuItem value="supplier">By Supplier</MenuItem>
                <MenuItem value="stock">By Stock Item</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Sort Options */}
        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
                name="sort_by"
                value={filters.sort_by}
                onChange={handleFilterChange}
                label="Sort By"
            >
                <MenuItem value="total_value">Total Value</MenuItem>
                <MenuItem value="quantity">Quantity</MenuItem>
                <MenuItem value="profit">Profit</MenuItem>
                <MenuItem value="cost">Cost Price</MenuItem>
                <MenuItem value="selling">Selling Price</MenuItem>
                <MenuItem value="stock_date">Stock Date</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Sort Order</InputLabel>
            <Select
                name="sort_order"
                value={filters.sort_order}
                onChange={handleFilterChange}
                label="Sort Order"
            >
                <MenuItem value="desc">Descending</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Generate Button */}
        <Grid item xs={12}>
            <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={generateReport}
            startIcon={<Search />}
            sx={{ mt: 2 }}
            >
            Generate Report
            </Button>
        </Grid>
        </Grid>

        {/* Active Filters */}
        {Object.keys(filters).some(key => filters[key] && !['report_type', 'sort_by', 'sort_order'].includes(key)) && (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Active Filters:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.supplier_id && (
                <Chip
                label={`Supplier: ${getCategoriesNameById(filters.supplier_id, referenceData.supplierNames)}`}
                onDelete={() => handleFilterChange({ target: { name: 'supplier_id', value: '' } })}
                color="primary"
                size="small"
                />
            )}
            {filters.stock_id && (
                <Chip
                label={`Stock: ${getCategoriesNameById(filters.stock_id, referenceData.stockNames)}`}
                onDelete={() => handleFilterChange({ target: { name: 'stock_id', value: '' } })}
                color="primary"
                size="small"
                />
            )}
            {filters.min_quantity && (
                <Chip
                label={`Min Qty: ${filters.min_quantity}`}
                onDelete={() => handleFilterChange({ target: { name: 'min_quantity', value: '' } })}
                color="secondary"
                size="small"
                />
            )}
            {filters.max_quantity && (
                <Chip
                label={`Max Qty: ${filters.max_quantity}`}
                onDelete={() => handleFilterChange({ target: { name: 'max_quantity', value: '' } })}
                color="secondary"
                size="small"
                />
            )}
            {filters.dateFrom && filters.dateTo && (
                <Chip
                label={`Date: ${filters.dateFrom} to ${filters.dateTo}`}
                onDelete={() => {
                    handleFilterChange({ target: { name: 'dateFrom', value: '' } });
                    handleFilterChange({ target: { name: 'dateTo', value: '' } });
                }}
                color="primary"
                size="small"
                />
            )}
            </Box>
        </Box>
        )}
    </Paper>

    {/* Report Summary Cards */}
    <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Items */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
        <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
            <Inventory sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma(reportData.summary.total_items || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Total Items
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Total Quantity */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
        <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
            <Storage sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma(reportData.summary.total_quantity || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Total Quantity
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Total Cost Value */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
        <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
            <AttachMoney sx={{ fontSize: 40, color: theme.palette.error.main, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(reportData.summary.total_cost_value || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Total Cost Value
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Total Selling Value */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
        <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
            <AttachMoney sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(reportData.summary.total_selling_value || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Total Selling Value
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Total Profit */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
        <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
            <TrendingUp sx={{ fontSize: 40, color: getProfitColor(reportData.summary.total_profit || 0), mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: getProfitColor(reportData.summary.total_profit || 0) }}>
                {formatCurrency(reportData.summary.total_profit || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Total Profit
            </Typography>
            </CardContent>
        </Card>
        </Grid>

        {/* Suppliers Count */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
        <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
            <LocalShipping sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma(reportData.summary.suppliers_count || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Suppliers
            </Typography>
            </CardContent>
        </Card>
        </Grid>
    </Grid>

    {/* Report Tabs */}
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ bgcolor: theme.palette.primary.main }}
        >
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <Summarize /> Detailed Report
            </Box>
            }
        />
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <BarChart /> Analysis
            </Box>
            }
        />
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <Analytics /> Insights
            </Box>
            }
        />
        </Tabs>

        {/* Tab 1: Detailed Report */}
        {activeTab === 0 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Detailed Inventory Report
            </Typography>
                        
            <TableContainer>
            <Table>
                <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Stock Date</strong></TableCell>
                    <TableCell><strong>Supplier</strong></TableCell>
                    <TableCell><strong>Stock Item</strong></TableCell>
                    <TableCell><strong>Quantity</strong></TableCell>
                    <TableCell><strong>Cost Price</strong></TableCell>
                    <TableCell><strong>Selling Price</strong></TableCell>
                    <TableCell><strong>Total Cost</strong></TableCell>
                    <TableCell><strong>Total Selling</strong></TableCell>
                    <TableCell><strong>Profit</strong></TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {reportData.details?.map((item, index) => {
                    const profit = calculateItemProfit(item);
                    const totalValue = calculateItemTotalValue(item);
                    return (
                    <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{new Date(item.stock_date).toLocaleDateString()}</TableCell>
                        <TableCell>{getCategoriesNameById(item.supplier_id, referenceData.supplierNames)}</TableCell>
                        <TableCell sx={{ fontWeight: 'medium' }}>{item.stock_name || 'N/A'}</TableCell>
                        <TableCell>
                        <Chip
                            label={formatNumberWithComma(item.quantity)}
                            color={getQuantityColor(item.quantity)}
                            variant="outlined"
                            size="small"
                        />
                        </TableCell>
                        <TableCell>{formatCurrency(item.cost)}</TableCell>
                        <TableCell>{formatCurrency(item.selling)}</TableCell>
                        <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {formatCurrency(item.quantity * item.cost)}
                        </Typography>
                        </TableCell>
                        <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: theme.palette.primary.main }}>
                            {formatCurrency(totalValue)}
                        </Typography>
                        </TableCell>
                        <TableCell>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                            fontWeight: 'bold',
                            color: getProfitColor(profit)
                            }}
                        >
                            {formatCurrency(profit)}
                        </Typography>
                        </TableCell>
                    </TableRow>
                    );
                })}
                
                {/* Footer Row with Grand Totals */}
                {reportData.details && reportData.details.length > 0 && (
                    <TableRow sx={{ 
                    backgroundColor: theme.palette.primary.dark,
                    '& td': {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        borderTop: `3px solid ${theme.palette.primary.main}`,
                        padding: '16px 8px'
                    }
                    }}>
                    <TableCell colSpan={4} align="center">
                        <Typography variant="h6" sx={{ color: 'white' }}>
                        GRAND TOTAL
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="h6" sx={{ color: 'white' }}>
                        {formatNumberWithComma(
                            reportData.details.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)
                        )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" sx={{ color: 'white', fontStyle: 'italic' }}>
                        {formatCurrency(
                            reportData.details.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0) / reportData.details.length
                        )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" sx={{ color: 'white', fontStyle: 'italic' }}>
                        {formatCurrency(
                            reportData.details.reduce((sum, item) => sum + (parseFloat(item.selling) || 0), 0) / reportData.details.length
                        )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="h6" sx={{ color: 'white' }}>
                        {formatCurrency(
                            reportData.details.reduce((sum, item) => 
                            sum + ((parseInt(item.quantity) || 0) * (parseFloat(item.cost) || 0)), 0)
                        )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="h6" sx={{ color: theme.palette.success.light }}>
                        {formatCurrency(
                            reportData.details.reduce((sum, item) => 
                            sum + ((parseInt(item.quantity) || 0) * (parseFloat(item.selling) || 0)), 0)
                        )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography 
                        variant="h6" 
                        sx={{ 
                            color: getProfitColor(
                            reportData.details.reduce((sum, item) => 
                                sum + calculateItemProfit(item), 0
                            )
                            )
                        }}
                        >
                        {formatCurrency(
                            reportData.details.reduce((sum, item) => sum + calculateItemProfit(item), 0)
                        )}
                        </Typography>
                    </TableCell>
                    </TableRow>
                )}
                </TableBody>
            </Table>
            </TableContainer>

            {(!reportData.details || reportData.details.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Inventory sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                No inventory items found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Try adjusting your filters
                </Typography>
            </Box>
            )}
        </Box>
        )}

        {/* Tab 2: Analysis */}
        {activeTab === 1 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Inventory Analysis
            </Typography>
            
            <Grid container spacing={3}>
            {/* Top Suppliers */}
            <Grid item xs={12} md={6}>
                <Card elevation={2}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping /> Top 5 Suppliers by Value
                    </Typography>
                    {reportData.topSuppliers?.slice(0, 5).map((supplier, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {supplier.supplier_name}
                        </Typography>
                        <Typography variant="h6" color="primary">
                            {formatCurrency(supplier.total_value)}
                        </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Items: {formatNumberWithComma(supplier.items_count)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Quantity: {formatNumberWithComma(supplier.total_quantity)}
                        </Typography>
                        </Box>
                        <LinearProgress 
                        variant="determinate" 
                        value={(supplier.total_value / (reportData.topSuppliers?.[0]?.total_value || 1)) * 100}
                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                        />
                    </Box>
                    ))}
                </CardContent>
                </Card>
            </Grid>


            {/* High Value Items */}
            <Grid item xs={12} md={6}>
                <Card elevation={2}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp /> High Value Items
                    </Typography>
                    {reportData.highValueItems?.slice(0, 5).map((item, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {item.stock_name}
                        </Typography>
                        <Typography variant="h6" color="success.main">
                            {formatCurrency(item.total_value)}
                        </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2">
                            Supplier: {item.supplier_name}
                        </Typography>
                        <Typography variant="body2">
                            Profit: {formatCurrency(item.profit)}
                        </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Cost: {formatCurrency(item.cost)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Selling: {formatCurrency(item.selling)}
                        </Typography>
                        </Box>
                    </Box>
                    ))}
                </CardContent>
                </Card>
            </Grid>

            {/* Low Quantity Alerts */}
            <Grid item xs={12} md={6}>
                <Card elevation={2}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning /> Low Quantity Alerts
                    </Typography>
                    {reportData.lowQuantityItems?.slice(0, 5).map((item, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {item.stock_name}
                        </Typography>
                        <Chip
                            label={formatNumberWithComma(item.quantity)}
                            color="warning"
                            size="small"
                        />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2">
                            Supplier: {item.supplier_name}
                        </Typography>
                        </Box>
                        <Alert severity="warning" sx={{ mt: 1 }}>
                        Stock Level: {getQuantityLevel(item.quantity)} - Reorder recommended
                        </Alert>
                    </Box>
                    ))}
                </CardContent>
                </Card>
            </Grid>
            </Grid>
        </Box>
        )}

        {/* Tab 3: Insights */}
        {activeTab === 2 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Business Insights
            </Typography>
            
            <Grid container spacing={3}>
            {/* Profitability Insights */}
            <Grid item xs={12} md={6}>
                <Card elevation={2}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assessment /> Profitability Analysis
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Overall Profit Margin
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                        <LinearProgress 
                            variant="determinate" 
                            value={
                            reportData.summary.total_selling_value > 0 
                                ? (reportData.summary.total_profit / reportData.summary.total_selling_value) * 100 
                                : 0
                            }
                            color={
                            (reportData.summary.total_profit / reportData.summary.total_selling_value) * 100 > 30 
                                ? "success" 
                                : (reportData.summary.total_profit / reportData.summary.total_selling_value) * 100 > 15 
                                ? "warning" 
                                : "error"
                            }
                            sx={{ height: 10, borderRadius: 5 }}
                        />
                        </Box>
                        <Typography variant="h6" sx={{ minWidth: 80 }}>
                        {reportData.summary.total_selling_value > 0 
                            ? `${((reportData.summary.total_profit / reportData.summary.total_selling_value) * 100).toFixed(1)}%`
                            : '0%'
                        }
                        </Typography>
                    </Box>
                    </Box>

                    {/* Average Metrics */}
                    <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Avg Cost per Item
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(reportData.summary.average_cost_per_item || 0)}
                        </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Avg Selling per Item
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                            {formatCurrency(reportData.summary.average_selling_per_item || 0)}
                        </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Avg Profit per Item
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                            {formatCurrency(
                            reportData.summary.total_quantity > 0 
                                ? reportData.summary.total_profit / reportData.summary.total_quantity 
                                : 0
                            )}
                        </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Avg Quantity per Item
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {reportData.summary.total_items > 0 
                            ? Math.round(reportData.summary.total_quantity / reportData.summary.total_items)
                            : 0
                            }
                        </Typography>
                        </Box>
                    </Grid>
                    </Grid>
                </CardContent>
                </Card>
            </Grid>

            {/* Efficiency Insights */}
            <Grid item xs={12} md={6}>
                <Card elevation={2}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Analytics /> Efficiency Metrics
                    </Typography>
                    
                    {/* Inventory Turnover */}
                    <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Inventory Value Distribution
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={3}>
                        <Box sx={{ p: 1, bgcolor: theme.palette.error.light, borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" display="block">
                            Cost
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {reportData.summary.total_selling_value > 0 
                                ? `${((reportData.summary.total_cost_value / reportData.summary.total_selling_value) * 100).toFixed(1)}%`
                                : '0%'
                            }
                            </Typography>
                        </Box>
                        </Grid>
                        <Grid item xs={3}>
                        <Box sx={{ p: 1, bgcolor: theme.palette.success.light, borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" display="block">
                            Profit
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {reportData.summary.total_selling_value > 0 
                                ? `${((reportData.summary.total_profit / reportData.summary.total_selling_value) * 100).toFixed(1)}%`
                                : '0%'
                            }
                            </Typography>
                        </Box>
                        </Grid>
                        <Grid item xs={3}>
                        <Box sx={{ p: 1, bgcolor: theme.palette.warning.light, borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" display="block">
                            Items
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatNumberWithComma(reportData.summary.total_items)}
                            </Typography>
                        </Box>
                        </Grid>
                        <Grid item xs={3}>
                        <Box sx={{ p: 1, bgcolor: theme.palette.info.light, borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="caption" display="block">
                            Quantity
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatNumberWithComma(reportData.summary.total_quantity)}
                            </Typography>
                        </Box>
                        </Grid>
                    </Grid>
                    </Box>

                    {/* Recommendations */}
                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Recommendations
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {reportData.lowQuantityItems?.length > 0 && (
                        <li>
                            <Typography variant="body2">
                            Reorder {reportData.lowQuantityItems.length} low stock items
                            </Typography>
                        </li>
                        )}
                        {reportData.topSuppliers?.[0] && (
                        <li>
                            <Typography variant="body2">
                            Focus on {reportData.topSuppliers[0].supplier_name} (top supplier)
                            </Typography>
                        </li>
                        )}
                        {reportData.highValueItems?.length > 0 && (
                        <li>
                            <Typography variant="body2">
                            Increase stock for high-value items
                            </Typography>
                        </li>
                        )}
                    </ul>
                    </Box>
                </CardContent>
                </Card>
            </Grid>
            </Grid>
        </Box>
        )}
    </Paper>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>
    </Container>
);
};

export default InventoryReport;



